import React, {
  useState,
  useEffect,
  useRef,
  cloneElement,
  useCallback,
} from 'react';

// Shared Components

import { Upload } from '@design-systems/icons';
import cx from 'classnames';
import pluralize from '@mc/fn/pluralize';
import useId from '@mc/hooks/useId';
import FeedbackBlock from '../FeedbackBlock';
import StackLayout from '../StackLayout';
import Text from '../Text';

import Button from '../Button';
import List, { ListItem } from '../List';
import { File } from './File';
import { TranslateFileInput } from './TranslateFileInput';
import stylesheet from './FileInput.css';
import { getReadableFileSize } from './utils';

type ErrorMessageProps = {
  error?: string;
  errorHeadline?: string;
  errorReason?: string | string[];
  emptyErrorState?: $TSFixMeFunction;
};

type FileState = string[];

type ErrorState = {
  isError?: boolean;
  errorReason?: string | string[];
  errorHeadline?: string;
};

/**
 * Displays an error message whenever the user uploads the wrong file type
 * @param errorReason
 * @returns {JSX.Element}
 * @constructor
 */
const ErrorMessage = ({
  errorReason,
  errorHeadline,
  error,
  emptyErrorState,
}: ErrorMessageProps) => {
  // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0.
  const { errorMsg } = TranslateFileInput();

  return (
    <FeedbackBlock
      className={cx(stylesheet.errorContainer)}
      variant="error"
      title={errorHeadline || errorMsg}
      onClose={emptyErrorState}
      inline
    >
      {Array.isArray(errorReason) && errorReason.length === 1
        ? errorReason[0]
        : null}
      {errorReason && errorReason.length > 1 && (
        <List appearance="small">
          {Array.isArray(errorReason) &&
            errorReason.map((value, index) => (
              <ListItem appearance="small" key={index}>
                {value}
              </ListItem>
            ))}
        </List>
      )}
      {error && <>{error}</>}
    </FeedbackBlock>
  );
};

const defaultOnUpload = (files: $TSFixMe) => Promise.resolve(files);

const defaultErrorState: ErrorState = {
  isError: false,
  errorReason: '',
  errorHeadline: '',
};

export type FileInputProps = {
  allowedExtensions?: string[];
  children?: React.ReactNode;
  description?: string | React.ReactNode;
  error?: string;
  label: string;
  maxFiles?: number;
  onChange: $TSFixMeFunction;
  onClick?: $TSFixMeFunction;
  onDelete?: $TSFixMeFunction;
  onUpload?: $TSFixMeFunction;
  showImagePreviews?: boolean;
  value?: $TSFixMe[];
  size?: 'default' | 'compact';
  disabled?: boolean;
  maxFileSize?: number; // in KB
  customActionButton?: React.ReactNode;
};

const FileInput = React.forwardRef<$TSFixMe, FileInputProps>(function FileInput(
  {
    description,
    label,
    error,
    onChange,
    onClick,
    onUpload = defaultOnUpload,
    onDelete,
    maxFiles = 1,
    allowedExtensions = [],
    value = [],
    children,
    showImagePreviews = false,
    size = 'default',
    disabled = false,
    maxFileSize,
    customActionButton,
  },
  forwardedRef,
) {
  const id = useId();
  const descriptionId = useId();
  // Contains a set of file names
  const [newFiles, setNewFiles] = useState<FileState>([]);

  const [errorState, setErrorState] = useState<ErrorState>(defaultErrorState);
  // Used to track if the user is dragging files over the input
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
    if (onClick && typeof onClick === 'function') {
      onClick();
    }
  };

  const formattedExtension = allowedExtensions
    .map((ext) => `.${ext}`)
    .join(', ');

  // Translate default text
  const {
    addFileMsg,
    addFilesMsg,
    dragAndDropLabel,
    errorRedesignTitle,
    errorRedesignMaxFileSingluar,
    errorRedesignMaxFilePlural,
    errorRedesignDuplicateFiles,
    errorRedesignServerError,
    errorRedesignFileType,
    errorRedesignFileSize,
  } = TranslateFileInput(
    maxFiles,
    formattedExtension,
    getReadableFileSize((maxFileSize ?? 0) * 1000),
  );

  /**
   * Used to resolve the file name
   * @param {*} obj
   * @returns string
   */
  const resolveFileName = useCallback((file: $TSFixMe) => file.name, []);

  /**
   * This function is used to reset the error state
   * It sets the error state to false and clears out the error reason and headline
   */
  const emptyErrorState = useCallback(() => {
    setErrorState(defaultErrorState);
  }, []);

  /**
   * This useEffect will run every time 'value' changes
   * The purpose is to update the 'newFiles' list whenever the 'value' property has been updated.
   * If a file name inside of the 'newFiles' list appears inside of the 'value' list,
   * then remove that file name from the 'newFiles' list.
   *
   * Context: When the user selects a file, those files are immediately added to the 'newFiles' list.
   * That list is used to render File Cards with a loading Icon. When the file has completely
   * gone through the 'onUpload' process and kicks off the 'onChange' function, the 'value' prop
   * will get updated with those new files. This function cleans up the 'newFiles' list.
   */
  useEffect(() => {
    if (newFiles.length) {
      // Creates a set of the existing file names
      const existingSet = new Set(value.map(resolveFileName));

      // Filter out files that already exist in the value array
      const filteredNewFiles = newFiles.filter(
        (fileName) => !existingSet.has(fileName),
      );

      // Only update if there's actually a difference
      if (filteredNewFiles.length !== newFiles.length) {
        setNewFiles(filteredNewFiles);
      }
    }
  }, [value, resolveFileName, newFiles]);

  /**
   * HandleSelect is the function used to take files the user has selected,
   * run it through the 'onUpload' function, and then execute the 'onChange' function.
   * Before any of the above functions can be executed. There are some constraints that
   * are checked first.
   *
   * Constraints
   *  - Max files (defaults to 1)
   *  - No Duplicate files
   *  - File type Validation
   *  - File size Validation
   */
  const handleSelectFile = (fileList: $TSFixMe) => {
    // Prevents the user from uploading more files than the limit
    if (fileList.length + value.length > maxFiles) {
      setErrorState({
        isError: true,
        errorReason: [
          maxFiles > 1
            ? errorRedesignMaxFilePlural
            : errorRedesignMaxFileSingluar,
        ],
        errorHeadline: errorRedesignTitle,
      });
      return;
    }

    /**
     * Using the 'newFiles' list and the 'value' list.
     * A set is created and checked against to see if the user is trying to
     * add any duplicate files.
     */
    const fileNameSet = new Set([...value.map(resolveFileName), ...newFiles]);
    const duplicateFileSet = new Set();
    for (const { name } of fileList) {
      if (fileNameSet.has(name)) {
        duplicateFileSet.add(name);
      }
    }

    if (duplicateFileSet.size) {
      const duplicateFilesArr = Array.from(duplicateFileSet);
      const duplicateFilesReason = duplicateFilesArr.map((fileName) => {
        return `"${fileName}" ${errorRedesignDuplicateFiles}`;
      });

      setErrorState({
        isError: true,
        errorReason: duplicateFilesReason,
        errorHeadline: errorRedesignTitle,
      });
      return;
    }

    // If the user is trying to upload a file type that isn't allowed. Works when pulling files in Drag and Drop state.
    if (allowedExtensions.length) {
      const fileTypeSet = new Set(allowedExtensions);
      const invalidFileTypeSet = new Set();

      for (const { name } of fileList) {
        const fileExtension = name.split('.').pop();
        if (!fileTypeSet.has(fileExtension)) {
          invalidFileTypeSet.add(name);
        }
      }

      if (invalidFileTypeSet.size) {
        let invalidFiles: string[] = [];
        const invalidFilesArr = Array.from(invalidFileTypeSet);
        invalidFiles = invalidFilesArr.map((fileName) => {
          return `"${fileName}" ${errorRedesignFileType}`;
        });

        setErrorState({
          isError: true,
          errorReason: invalidFiles,
          errorHeadline: errorRedesignTitle,
        });
        return;
      }
    }

    // If the user is trying to upload a file that exceeds the max file size
    // Considering user provides maxFileSize in kb and converting it into bytes to validate
    if (maxFileSize) {
      const maxFileSizeInBytes = maxFileSize * 1000;

      const invalidFileSize = fileList.filter((file: $TSFixMe) => {
        return file.size > maxFileSizeInBytes;
      });

      if (invalidFileSize.length) {
        const invalidFiles: string[] = [];
        invalidFileSize.forEach((file: $TSFixMe) => {
          invalidFiles.push(`"${file.name}" ${errorRedesignFileSize}`);
        });
        setErrorState({
          isError: true,
          errorReason: invalidFiles,
          errorHeadline: errorRedesignTitle,
        });
        return;
      }
    }

    // If none of the above constraints aren't triggered reset error state
    setErrorState(defaultErrorState);

    setNewFiles(fileList.map(resolveFileName));
    /**
     * If the user is trying to upload multiple files at once
     * The 'onUpload' function needs to be called for each file
     * and the results need to be handled individually
     */
    const promiseArray = fileList.map((file: $TSFixMe) => onUpload(file));
    Promise.allSettled(promiseArray).then((results) => {
      const newResults = results.map((result) => {
        if (result.status === 'fulfilled') {
          const fileData = result.value;
          fileData.error = '';
          return fileData;
        }
        const fileData = result.reason.file || result.reason;
        // If an error message exists, return it; otherwise, use the translated generic error message.
        const errMessage = result.reason.error?.message || undefined;
        if (errMessage) {
          fileData.error = errMessage;
        } else {
          fileData.error = errorRedesignServerError;
        }
        return fileData;
      });
      onChange([...value, ...newResults]);
    });
  };

  /**
   * This function is used to retry the upload of a file
   */

  const handleFileRetry = (
    index: $TSFixMe,
    event?: React.MouseEvent | React.KeyboardEvent,
  ) => {
    if (
      !event ||
      event.type === 'click' ||
      (event.type === 'keydown' &&
        'key' in event &&
        (event.key === 'Enter' || event.key === ' '))
    ) {
      const resultFiles = value.slice();
      const file = resultFiles[index];
      onUpload(file)
        .then((results: $TSFixMe) => {
          results.error = '';
          resultFiles[index] = results;
          onChange(resultFiles);
        })
        .catch((err: $TSFixMe) => {
          const fileData = err.file || err;
          const errMessage = err.error.message || undefined;
          if (errMessage) {
            fileData.error = errMessage;
          }
          return fileData;
        });
    }
  };

  /**
   * Removes a file from the list by its index
   * */
  const handleRemoveFile = (
    index: $TSFixMe,
    event?: React.MouseEvent | React.KeyboardEvent,
  ) => {
    if (
      !event ||
      event.type === 'click' ||
      (event.type === 'keydown' &&
        'key' in event &&
        (event.key === 'Enter' || event.key === ' '))
    ) {
      const resultFiles = value.slice();
      if (onDelete) {
        onDelete(resultFiles[index], index);
      }
      resultFiles.splice(index, 1);
      onChange(resultFiles);
    }
  };

  /**
   * If there's a list of allowedExtensions they get converted into a string thats passed
   * into the inputs accept prop
   *
   * If allowedExtensions does not exist undefined is passed
   * */
  const acceptedFiles = allowedExtensions.length
    ? allowedExtensions
        .map((extension) => `.${extension.toLowerCase()}`)
        .join(',')
    : undefined;

  /**
   * Renders out the default file input in the event
   * the user hasn't passed in a child component
   * For readability this function was created
   * @returns [<File/>] list of File component
   */
  const renderDefaultFile = () =>
    value.map((file, index) => {
      return (
        <File
          key={index}
          file={file}
          onRemove={(e) => handleRemoveFile(index, e)}
          showImagePreviews={showImagePreviews}
          size={size}
          onRetry={(e) => handleFileRetry(index, e)}
        />
      );
    });

  /**
   * This function renders out the passed in Child component
   * The file object, the index, and the onRemove function are passed as props.
   * This ensures that values produced by the child component are in sync with the
   * array for the file input
   *
   * For readability this function was created
   * @returns Components
   */
  const renderChildComponent = () => {
    return value.map((file, index) => {
      // @ts-expect-error TS(2769) FIXME: No overload matches this call.
      return cloneElement(children, {
        key: index,
        file,
        index,
        onRemove: (e: React.MouseEvent | React.KeyboardEvent) =>
          handleRemoveFile(index, e),
        onRetry: (e: React.MouseEvent | React.KeyboardEvent) =>
          handleFileRetry(index, e),
      });
    });
  };

  /**
   * This function is used to handle the drag and drop events
   * It sets the dragging state to true when a file is dragged over the input
   * and sets it back to false when the file is dropped or dragged out
   */
  const handleDragEnter = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const fileList = e.dataTransfer.files
      ? Array.from(e.dataTransfer.files)
      : [];
    handleSelectFile(fileList);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  /**
   * This useEffect is used to reset the error state and revert the dragging state
   * when the user clears out the input
   * */
  useEffect(() => {
    if (!value.length) {
      setIsDragging(false);
      // Reset error state when clearing input
      setErrorState((prev) => {
        if (prev.isError) {
          return defaultErrorState;
        }
        return prev;
      });
    }
  }, [value]);

  /**
   * This function is used to handle the change event of the file input
   * It converts the file list into an array and passes it into the handleSelectFile function
   * */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files ? Array.from(e.target.files) : [];
    handleSelectFile(fileList);
  };

  /**
   * This function is used to trigger the file input when the user presses Enter or Space
   * It prevents the default action and calls the handleClick function
   * */
  const triggerFileInput = (e: React.KeyboardEvent<HTMLElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && e.currentTarget === e.target) {
      e.preventDefault();
      handleClick();
    }
  };

  const renderUploadAction = () => {
    if (value.length || isDragging || newFiles.length) {
      return null;
    }

    if (customActionButton) {
      return (
        <div onClick={(e) => e.stopPropagation()}>{customActionButton}</div>
      );
    }

    return (
      <Button
        type="secondary"
        htmlFor={id}
        disabled={disabled}
        onKeyDown={triggerFileInput}
      >
        {pluralize(addFileMsg, addFilesMsg, maxFiles)}
      </Button>
    );
  };

  return (
    <div className={stylesheet.parentContainer} ref={forwardedRef}>
      {errorState.isError || error ? (
        <ErrorMessage
          {...errorState}
          error={error}
          emptyErrorState={emptyErrorState}
        />
      ) : null}
      <Button
        htmlType="button"
        htmlFor={id}
        type="quaternary"
        className={cx(stylesheet.container, {
          [stylesheet[size]]: size !== 'default',
          [stylesheet.drag]: isDragging,
          [stylesheet.containerDisabled]: disabled,
        })}
        onClick={handleClick}
        disabled={disabled}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={(e) => {
          // Only set isDragging to false if leaving the whole button, not a child
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            handleDragLeave(e);
          }
        }}
        onKeyDown={triggerFileInput}
      >
        <StackLayout className={stylesheet.secondaryContainer}>
          {(size === 'default' || (size === 'compact' && isDragging)) &&
            (isDragging ? (
              <Upload color="var(--color-icon-primary)" size="medium" />
            ) : (
              !value.length &&
              !newFiles.length &&
              size === 'default' && (
                <Upload color="var(--color-icon-primary)" size="medium" />
              )
            ))}
          <StackLayout gap={0} className={stylesheet.content}>
            <Text className={stylesheet.title}>
              {isDragging ? dragAndDropLabel : label}
            </Text>
            {!isDragging && typeof description === 'string' ? (
              <Text className={stylesheet.description} id={descriptionId}>
                {description}
              </Text>
            ) : (
              !isDragging && (
                <div className={stylesheet.description} id={descriptionId}>
                  {description}
                </div>
              )
            )}
          </StackLayout>

          {/* Hidden file input */}
          <input
            className={stylesheet.fileinput}
            id={id}
            key={value && value.map(resolveFileName).join(';')}
            type="file"
            onChange={handleFileInputChange}
            accept={acceptedFiles}
            multiple={maxFiles > 1}
            ref={fileInputRef}
            aria-hidden="true"
            tabIndex={-1}
          />
          {/* Hide button if a file is uploaded or file comes into scope */}
          {renderUploadAction()}

          {(newFiles.length > 0 || value.length > 0) && (
            <div
              className={stylesheet.fileGroup}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Newly added Files that are loading */}
              {newFiles.map((fileName, index) => (
                <File
                  key={index}
                  file={{ name: fileName }}
                  isLoading={true}
                  showImagePreviews={showImagePreviews}
                  size={size}
                />
              ))}

              {/* Uploaded file(s) */}
              {children ? renderChildComponent() : renderDefaultFile()}
            </div>
          )}
        </StackLayout>
      </Button>
    </div>
  );
});

export default FileInput;
